/* prettier-ignore */

/**
 * HRS-2990: modo del informador (técnico de turismo). Sustituye al booleano
 * `isTechMode`, que mezclaba tres flujos distintos en un único interruptor.
 *
 * - `simple`: registro rápido del visitante (email + PDFs). No pasa por el
 *   planner y no crea ruta.
 * - `complete`: registro + email con los recursos que el técnico ha ido
 *   añadiendo. Tampoco crea ruta (el `+` alimenta el email, no la ruta).
 * - `predefined-route`: crear una ruta predefinida. Es el comportamiento
 *   histórico de `?techMode=true`.
 *
 * HRS-3145: la entrada es la ruta `/tech-mode/...` (ver `helpers/tech-mode-entry.ts`).
 * `?techMode=true` e `?informer=<modo>` se mantienen por los QR impresos y
 * redirigen a esa ruta (`legacyInformerParamsGuard`).
 *
 * `null` = visitante normal (planner del turista): no debe cambiar de
 * comportamiento por este ticket.
 */
export type InformerMode = 'simple' | 'complete' | 'predefined-route';

/** Valor de `?informer=` en la URL → modo (entrada heredada, redirige a `/tech-mode`). */
export const INFORMER_MODE_QUERY_PARAM = 'informer';

/** Clave con la que se persiste el modo (mismo TTL que el resto de params de entrada). */
export const INFORMER_MODE_STORAGE_KEY = 'informerMode';

export const INFORMER_MODES: InformerMode[] = ['simple', 'complete', 'predefined-route'];

/**
 * Registro configurado que se quiere usar, en `?register=`. Lo pone el acceso
 * directo del panel del CMS (`?informer=complete&register={id}`) para que el
 * formulario pida los campos de ESE registro y no los del registro de la app.
 * En el registro simple el id viaja en la ruta (`tech-register-by-id/:id`).
 */
export const INFORMER_REGISTER_QUERY_PARAM = 'register';

export const INFORMER_REGISTER_STORAGE_KEY = 'informerRegisterId';

/** Recursos añadidos para el correo del visitante (modo `complete`). */
export const INFORMER_RESOURCES_STORAGE_KEY = 'informerResources';

/** Contenidos añadidos a la ruta que se está creando (modo `predefined-route`). */
export const INFORMER_ROUTE_ITEMS_STORAGE_KEY = 'informerRouteItems';

/**
 * Datos del formulario que deben quedar como estadística de la ruta predefinida
 * (tipo de viajero e intereses). Se guardan al enviar el formulario y se
 * consumen al guardar la ruta, que es cuando existe el token al que asociarlos.
 */
export const INFORMER_ROUTE_REGISTER_STORAGE_KEY = 'informerRouteRegister';

/** Valida un valor de origen externo (query param / localStorage) como modo. */
export function toInformerMode(value: unknown): InformerMode | null {
  return INFORMER_MODES.includes(value as InformerMode) ? value as InformerMode : null;
}

/**
 * Valida el id del registro: solo dígitos, porque va directo a la query de
 * `get-register-app`.
 */
export function toInformerRegisterId(value: unknown): string | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;

  const id = String(value).trim();
  return /^\d+$/.test(id) ? id : null;
}
